/**
 * Pricing → Precios · **Control de Precios** (`/pricing/precios`).
 *
 * Puerto de `Pricing::precios` + `views/pricing/precios.php` (840 líneas con su
 * JavaScript), reordenado para que la pantalla no sea una lista larga de arriba
 * abajo:
 *
 * - Una **barra de filtros pegada arriba** (ubicación, fecha, búsqueda y
 *   categoría) que sirve a las cuatro secciones, con el resumen de lo que se está
 *   viendo en contadores pulsables.
 * - Cuatro **secciones** que se abren de una en una: *Cambios* (el detector de
 *   diferencias), *Precios* (la tabla del día), *Competencia* (alertas y
 *   estaciones en seguimiento) y *Análisis* (los gráficos).
 *
 * El panel anterior tenía las tres primeras apiladas en una sola página y los
 * listados largos sin desplazamiento propio, así que había que bajar y bajar; cada
 * sección de aquí se queda con su propio scroll.
 *
 * Diferencias anotadas:
 * - El panel anterior descargaba todo el histórico (~1,6 MB) y comparaba en el
 *   navegador; aquí la comparación la resuelve la API (`GET /pricing/comparar`)
 *   con el fichero de cada fecha.
 * - Se puede marcar **la estación completa** (todos sus productos), no sólo
 *   producto a producto.
 * - Se añade una sección de **análisis** con gráficos, que el panel anterior no
 *   tenía en esta pantalla.
 */

import {
  ArrowLeft,
  BarChart3,
  Building2,
  ListFilter,
  Search,
  Tags,
  TrendingUp,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { ConfirmDialog } from '@/components/ui/dialog';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { cn, formatNumber } from '@/lib/utils';

import { PreciosAnalisis } from '../components/precios-analisis';
import { PreciosCambios } from '../components/precios-cambios';
import { PreciosFiltros } from '../components/precios-filtros';
import { PreciosSeguimiento } from '../components/precios-seguimiento';
import { PreciosTabla } from '../components/precios-tabla';
import {
  useCompararPricing,
  useCompetenciaPricing,
  useEliminarCompetidor,
  useEliminarCompetidorEstacion,
  useGuardarCompetidor,
  useGuardarCompetidorEstacion,
  useMarcarAlerta,
  usePreciosPricing,
  usePricingEstadisticas,
  useVerificarCompetidores,
} from '../pricing.api';
import type { CompetidorPricing } from '../pricing.types';
import {
  claveCompetidor,
  coincideBusqueda,
  hoy,
  mensajeDe,
  normalizar,
  partesDeUbicacion,
} from '../pricing-utils';

type Seccion = 'cambios' | 'precios' | 'competencia' | 'analisis';

/** Secciones de la pantalla, en el orden en que se suelen mirar. */
const SECCIONES: { clave: Seccion; etiqueta: string; icono: typeof Search; detalle: string }[] = [
  {
    clave: 'cambios',
    etiqueta: 'Cambios',
    icono: ListFilter,
    detalle: 'Diferencias entre los dos últimos ficheros',
  },
  {
    clave: 'precios',
    etiqueta: 'Precios',
    icono: Tags,
    detalle: 'Precios del día por estación',
  },
  {
    clave: 'competencia',
    etiqueta: 'Competencia',
    icono: Building2,
    detalle: 'Alertas y estaciones en seguimiento',
  },
  {
    clave: 'analisis',
    etiqueta: 'Análisis',
    icono: BarChart3,
    detalle: 'Gráficos de precios y cambios',
  },
];

/** Estación que se está quitando del seguimiento. */
interface EstacionAQuitar {
  estacion: string;
  productos: number;
}

export function PricingPreciosPage() {
  useDocumentTitle('Pricing · Control de Precios');

  const [seccion, setSeccion] = useState<Seccion>('cambios');
  const [ubicacion, setUbicacion] = useState('');
  // Vacío = «la fecha más reciente que tenga datos» (el bot no publica todos los días).
  const [fecha, setFecha] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [categoria, setCategoria] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  const [aQuitar, setAQuitar] = useState<EstacionAQuitar | null>(null);

  const estadisticas = usePricingEstadisticas();
  const competencia = useCompetenciaPricing();

  const ubicaciones = estadisticas.data?.estadisticas.ubicaciones ?? [];
  const categorias = Object.keys(estadisticas.data?.estadisticas.categorias ?? {});

  // La primera ubicación disponible es la que compara el panel anterior al abrir.
  const ubicacionElegida = ubicacion || ubicaciones[0] || '';
  const filtroUbicacion = partesDeUbicacion(ubicacionElegida) ?? {
    departamento: '',
    provincia: '',
    distrito: '',
  };

  const comparacion = useCompararPricing(filtroUbicacion, Boolean(ubicacionElegida));
  // Sin fecha elegida se usa la del último fichero de la ubicación.
  const fechaElegida = fecha || comparacion.data?.fecha_nueva || hoy();
  const precios = usePreciosPricing(
    { ...filtroUbicacion, fecha: fechaElegida },
    Boolean(ubicacionElegida),
  );

  const marcarProducto = useGuardarCompetidor();
  const marcarEstacion = useGuardarCompetidorEstacion();
  const quitarProducto = useEliminarCompetidor();
  const quitarEstacion = useEliminarCompetidorEstacion();
  const verificar = useVerificarCompetidores();
  const marcarAlerta = useMarcarAlerta();

  const ocupado =
    marcarProducto.isPending ||
    marcarEstacion.isPending ||
    quitarProducto.isPending ||
    quitarEstacion.isPending;

  // Memoizado para que los `Set` de seguimiento no se recompongan en cada render.
  const competidores = useMemo(() => competencia.data?.competidores ?? [], [competencia.data]);

  /** Competidor de cada `ESTACION|PRODUCTO` en seguimiento (para poder quitarlo). */
  const porProducto = useMemo(
    () =>
      new Map(
        competidores.map((item) => [claveCompetidor(item.estacion, item.producto), item] as const),
      ),
    [competidores],
  );

  /** Estaciones en seguimiento. */
  const estacionesMarcadas = useMemo(
    () => new Set(competidores.map((item) => normalizar(item.estacion))),
    [competidores],
  );

  /** Precios del filtro (búsqueda y categoría) que comparten tabla y análisis. */
  const preciosFiltrados = useMemo(
    () =>
      (precios.data?.data ?? []).filter(
        (fila) =>
          coincideBusqueda(busqueda, fila.estacion, fila.producto, fila.categoria) &&
          (!categoria || fila.categoria === categoria),
      ),
    [busqueda, categoria, precios.data],
  );

  const cambios = comparacion.data;
  const totalesCambios = cambios?.totales;

  /** Da de alta todos los productos de una estación. */
  function onMarcarEstacion(estacion: string) {
    marcarEstacion.mutate(
      { estacion, ...filtroUbicacion },
      {
        onSuccess: (respuesta) =>
          setAviso(
            `«${estacion}» quedó en seguimiento con ${respuesta.productos} productos (${respuesta.creados} nuevos, ${respuesta.actualizados} actualizados) según los precios del ${respuesta.fecha}.`,
          ),
        onError: (error) => setAviso(mensajeDe(error, 'No se pudo marcar la estación completa.')),
      },
    );
  }

  /** Da de alta un único producto de una estación. */
  function onMarcarProducto(datos: {
    estacion: string;
    producto: string;
    direccion?: string;
    precio?: number;
  }) {
    marcarProducto.mutate(
      { ...datos, ...filtroUbicacion },
      {
        onSuccess: (respuesta) =>
          setAviso(
            respuesta.actualizado
              ? `«${datos.producto}» de ${datos.estacion} se actualizó en el seguimiento.`
              : `«${datos.producto}» de ${datos.estacion} se agregó al seguimiento.`,
          ),
        onError: (error) => setAviso(mensajeDe(error, 'No se pudo marcar el producto.')),
      },
    );
  }

  /** Quita un producto del seguimiento. */
  function onQuitarProducto(competidor: CompetidorPricing) {
    quitarProducto.mutate(competidor.id, {
      onSuccess: () =>
        setAviso(`«${competidor.producto}» de ${competidor.estacion} salió del seguimiento.`),
    });
  }

  /** Pide confirmación antes de quitar una estación completa. */
  function onQuitarEstacion(estacion: string) {
    const productos = competidores.filter(
      (item) => normalizar(item.estacion) === normalizar(estacion),
    ).length;

    setAQuitar({ estacion, productos });
  }

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/pricing"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Pricing
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Tags className="size-6 text-brand-500" aria-hidden="true" />
          Control de Precios
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Detector de cambios entre las dos últimas consultas, precios del día, seguimiento de la
          competencia y análisis por producto.
        </p>
      </div>

      {cambios?.suficiente ? (
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">{formatNumber(cambios.totales.cambios)} cambios</Badge>
          <Badge variant={(competencia.data?.no_leidas ?? 0) > 0 ? 'warning' : 'success'}>
            {formatNumber(competencia.data?.no_leidas ?? 0)} alertas sin leer
          </Badge>
        </div>
      ) : null}
    </header>
  );

  return (
    <div className="space-y-5">
      {cabecera}

      <PreciosFiltros
        ubicaciones={ubicaciones}
        ubicacion={ubicacionElegida}
        onUbicacion={setUbicacion}
        fecha={fechaElegida}
        fechaMaxima={hoy()}
        onFecha={setFecha}
        busqueda={busqueda}
        onBusqueda={setBusqueda}
        categorias={categorias}
        categoria={categoria}
        onCategoria={setCategoria}
        onActualizar={() => void comparacion.refetch()}
        actualizando={comparacion.isFetching}
        resumen={{
          cambios: totalesCambios?.cambios ?? 0,
          aumentos: totalesCambios?.aumentos ?? 0,
          bajadas: totalesCambios?.bajadas ?? 0,
          nuevos: totalesCambios?.nuevos ?? 0,
          quitados: totalesCambios?.quitados ?? 0,
          competidores: estacionesMarcadas.size,
          alertas: competencia.data?.no_leidas ?? 0,
        }}
        onVerCambios={() => setSeccion('cambios')}
        onVerCompetencia={() => setSeccion('competencia')}
      />

      <nav className="flex gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:pb-0" aria-label="Secciones del control de precios">
        {SECCIONES.map((item) => (
          <button
            key={item.clave}
            type="button"
            onClick={() => setSeccion(item.clave)}
            aria-current={seccion === item.clave ? 'page' : undefined}
            className={cn(
              'inline-flex shrink-0 flex-col items-start rounded-control border px-3 py-2 text-left transition-colors',
              seccion === item.clave
                ? 'border-brand-300 bg-brand-50'
                : 'border-ink-100 bg-white hover:border-brand-200',
            )}
          >
            <span
              className={cn(
                'inline-flex items-center gap-1.5 text-xs font-semibold',
                seccion === item.clave ? 'text-brand-700' : 'text-ink-700',
              )}
            >
              <item.icono className="size-3.5" aria-hidden="true" />
              {item.etiqueta}
            </span>
            <span className="text-[11px] text-ink-400">{item.detalle}</span>
          </button>
        ))}
      </nav>

      {aviso ? (
        <Alert variant="success" title="Seguimiento actualizado">
          <span className={ICONO_EN_ALERTA}>
            <TrendingUp className="size-4" aria-hidden="true" />
            {aviso}
          </span>
        </Alert>
      ) : null}

      {seccion === 'cambios' ? (
        <PreciosCambios
          comparacion={cambios}
          cargando={comparacion.isLoading}
          error={comparacion.error}
          busqueda={busqueda}
          estacionesMarcadas={estacionesMarcadas}
          porProducto={porProducto}
          ocupado={ocupado}
          onMarcarEstacion={onMarcarEstacion}
          onQuitarEstacion={onQuitarEstacion}
          onMarcarProducto={onMarcarProducto}
          onQuitarProducto={onQuitarProducto}
        />
      ) : null}

      {seccion === 'precios' ? (
        <PreciosTabla
          precios={preciosFiltrados}
          total={preciosFiltrados.length}
          ultimaFecha={precios.data?.ultima_fecha}
          cargando={precios.isLoading}
          error={precios.error}
          ubicacion={ubicacionElegida.replace(/_/g, ' / ')}
          estacionesMarcadas={estacionesMarcadas}
          porProducto={porProducto}
          ocupado={ocupado}
          onMarcarEstacion={onMarcarEstacion}
          onQuitarEstacion={onQuitarEstacion}
          onMarcarProducto={onMarcarProducto}
          onQuitarProducto={onQuitarProducto}
        />
      ) : null}

      {seccion === 'competencia' ? (
        <PreciosSeguimiento
          competidores={competidores}
          alertas={competencia.data?.alertas ?? []}
          noLeidas={competencia.data?.no_leidas ?? 0}
          cargando={competencia.isLoading}
          ocupado={ocupado}
          puedeVerificar={Boolean(ubicacionElegida)}
          onVerificar={() =>
            verificar.mutate(filtroUbicacion, {
              onSuccess: (respuesta) =>
                setAviso(
                  respuesta.cambios.length === 0
                    ? `Se revisaron ${respuesta.competidores} estaciones con los precios del ${respuesta.fecha} y ninguna cambió.`
                    : `Se revisaron ${respuesta.competidores} estaciones: ${respuesta.cambios.length} productos con cambio de precio.`,
                ),
              onError: (error) => setAviso(mensajeDe(error, 'No se pudo verificar.')),
            })
          }
          onMarcarTodas={() => marcarAlerta.mutate('todas')}
          onMarcarAlerta={(id) => marcarAlerta.mutate(id)}
          onQuitarEstacion={onQuitarEstacion}
          onQuitarProducto={onQuitarProducto}
        />
      ) : null}

      {seccion === 'analisis' ? (
        <PreciosAnalisis precios={preciosFiltrados} comparacion={cambios} />
      ) : null}

      {aQuitar ? (
        <ConfirmDialog
          open
          onClose={() => setAQuitar(null)}
          onConfirm={() =>
            quitarEstacion.mutate(aQuitar.estacion, {
              onSuccess: () => {
                setAviso(`«${aQuitar.estacion}» salió del seguimiento.`);
                setAQuitar(null);
              },
            })
          }
          title="¿Quitar la estación del seguimiento?"
          description={`«${aQuitar.estacion}» dejará de vigilarse con sus ${aQuitar.productos} productos y no generará más alertas. Las alertas anteriores se conservan.`}
          confirmLabel="Quitar estación"
        />
      ) : null}
    </div>
  );
}
